/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PrivateRoutePath } from 'routes/paths';
import { SimpleHeader, OptionsButtons, Spinner } from 'ui/components';
import * as ParentalControlDuck from 'core/store/ducks/parentalControl';
import translate from '../../../services/i18n/translate';
import { EOptionType, IOption } from '../../../types/components/Settings';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const ManageAccessibilityPage: React.FunctionComponent = () => {
  const dispatch = useDispatch();

  const currentUser = useCurrentUser();

  // State for action
  const [activeAction, setActiveAction] = useState<EOptionType>();
  // Updating Store States
  const isLoading = useSelector(ParentalControlDuck.Selectors.isProcessing);
  const isComplete = useSelector(ParentalControlDuck.Selectors.isProcessed);

  // Defines the title and backLink
  const prepareHeaderProps = {
    subtitle: translate.settingsSubtitles,
    backLabel: translate['menu.profile.settings'],
    backUrl: PrivateRoutePath.SETTINGS,
  };

  // State for Loading
  const [isLoadingSubtitles, setIsLoadingSubtitles] = useState<boolean>(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState<boolean>(false);

  // State for setting active button
  const [activeSubtitleOption, setActiveSubtitleOption] = useState<boolean>(false);
  const [activeAudioOption, setActiveAudioOption] = useState<boolean>(false);

  // SUBTITLE - Buttons Default State
  const [subtitleOptionButtons, setSubtitleOptionButtons] = useState<IOption[]>([]);
  // SUBTITLE - Update Buttons on change state
  useEffect(() => {
    setSubtitleOptionButtons([
      {
        text: translate.buttonOff,
        key: 'subtitlesOn',
        type: EOptionType.ACTIVATE_SUBTITLE,
        value: false,
        isSelected: activeSubtitleOption === false,
        disabled: false,
      },
      {
        text: translate.buttonOn,
        key: 'subtitlesOff',
        type: EOptionType.ACTIVATE_SUBTITLE,
        value: true,
        isSelected: activeSubtitleOption === true,
        disabled: false,
      },
    ]);
  }, [activeSubtitleOption]);
  // SUBTITLE - Buttons Initi state on page load
  useEffect(() => {
    if (currentUser?.accessibilitySubtitles !== undefined) {
      setActiveSubtitleOption(currentUser?.accessibilitySubtitles);
    }
  }, [currentUser?.accessibilitySubtitles]);
  // SUBTITLE - Update Effects
  useEffect(() => {
    if (activeAction === EOptionType.ACTIVATE_SUBTITLE) {
      if (isLoading) {
        setIsLoadingSubtitles(true);
      } else if (isComplete) {
        setIsLoadingSubtitles(false);
      }
    }
  }, [isLoading, isComplete]);

  // AUDIO - Buttons Default State
  const [audioOptionButtons, setAudioOptionButtons] = useState<IOption[]>([]);
  // AUDIO - Update Buttons on change state
  useEffect(() => {
    setAudioOptionButtons([
      {
        text: translate.buttonOff,
        key: 'audioDescriptionOff',
        type: EOptionType.AUDIO_DESCRIPTION,
        value: false,
        isSelected: activeAudioOption === false,
        disabled: false,
      },
      {
        text: translate.buttonOn,
        key: 'audioDescriptionOn',
        type: EOptionType.AUDIO_DESCRIPTION,
        value: true,
        isSelected: activeAudioOption === true,
        disabled: false,
      },
    ]);
  }, [activeAudioOption]);
  // AUDIO - Buttons init state on Page Load
  useEffect(() => {
    if (currentUser?.accessibilityAudioDescription !== undefined) {
      setActiveAudioOption(currentUser?.accessibilityAudioDescription);
    }
  }, [currentUser?.accessibilityAudioDescription]);
  // AUDIO - Update Effects
  useEffect(() => {
    if (activeAction === EOptionType.AUDIO_DESCRIPTION) {
      if (isLoading) {
        setIsLoadingAudio(true);
      } else if (isComplete) {
        setIsLoadingAudio(false);
      }
    }
  }, [isLoading, isComplete]);

  const changeSubtitleOption = useCallback(
    (option: IOption) => {
      setActiveAction(EOptionType.ACTIVATE_SUBTITLE);
      dispatch(ParentalControlDuck.ActionCreators.changeAccessibilitySubtitles(option.value === true));
    },
    [dispatch]
  );

  const changeAudioOption = useCallback(
    (option: IOption) => {
      setActiveAction(EOptionType.AUDIO_DESCRIPTION);
      dispatch(ParentalControlDuck.ActionCreators.changeAccessibilityAudioDescription(option.value === true));
    },
    [dispatch]
  );

  return (
    <div className="template-holder back-nav component-inner component-inner-sub-settings" id="Settings_accessibility">
      <SimpleHeader
        subtitle={prepareHeaderProps.subtitle}
        backLabel={prepareHeaderProps.backLabel}
        backUrl={prepareHeaderProps.backUrl}
      />
      <div className="setting-container">
        <p className="description">{translate.settingsAccessibilitySubtitles}</p>
        <div className="container-options playback">
          <OptionsButtons
            options={subtitleOptionButtons}
            onClickCallback={changeSubtitleOption}
            bemBlock="AccessibilitySettings"
          />
        </div>
        <div className="spinner-wrapper">
          <Spinner isActive={isLoadingSubtitles} isEmbedded={false} />
        </div>
      </div>
      <div className="setting-separator" style={{ height: '50px' }} />
      <div className="setting-container">
        <h2>{translate.settingsAudioDescription}</h2>
        <p className="description">{translate.settingsAccessibilityAudioDescriptions}</p>
        <div className="container-options playback">
          <OptionsButtons
            options={audioOptionButtons}
            onClickCallback={changeAudioOption}
            bemBlock="AccessibilitySettings"
          />
        </div>
        <div className="spinner-wrapper">
          <Spinner isActive={isLoadingAudio} isEmbedded={false} />
        </div>
      </div>
    </div>
  );
};

export default ManageAccessibilityPage;
